import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';

import { addSnackbarAlert } from '@sternum/ui';
import { routes } from '@sternum/web/config/routes';
import { appStorage } from '@sternum/web/services/appStorage';
import { namespaces } from '@sternum/web/services/i18n';
import { trpc, TRPCReactQueryError } from '@sternum/web/services/trpc';

export function useRedirectToDefaultPath() {
  const navigate = useNavigate();
  const trpcUtils = trpc.useUtils();
  const { t } = useTranslation(namespaces.common);

  const redirectToDefaultPath = useCallback(
    async (clientId: string) => {
      try {
        const context = { trpc: { context: { clientId } } } as never;

        const lastLocationPath = appStorage.getLastLocationPath();

        if (lastLocationPath) {
          appStorage.removeLastLocationPath();
          navigate(lastLocationPath);

          return;
        }

        const response = await trpcUtils.systemClient.getClient.fetch(clientId, context);
        const mode = response?.mode;

        let path: string;

        if (mode === 'compute') {
          const { total } = await trpcUtils.scsAlertsStatistics.getSCSTotalProtectedVMs.fetch({}, context);

          if (total) {
            path = routes.workloads.absolute;
          } else {
            path = routes.integrations.cloud.absolute;
          }
        } else {
          const products = await trpcUtils.product.getProductsWithVersions.fetch({}, context);

          if (products.items.length) {
            path = routes.executiveSummary.absolute;
          } else {
            path = routes.integrations.devices.absolute;
          }
        }

        navigate(generatePath(path, { clientId }));
      } catch (e) {
        const error = e as TRPCReactQueryError;
        const isUnauthorizedError = error.data?.code === 'UNAUTHORIZED';

        if (!isUnauthorizedError) {
          addSnackbarAlert({
            variant: 'error',
            message: t('generalError'),
          });
        }
      }
    },
    [navigate, trpcUtils, t],
  );

  return { redirectToDefaultPath };
}
